"use client";

import { useEffect } from "react";
import Script from "next/script";
import { COOKIE_CONSENT_KEY, getCookieConsent, type ConsentValue } from "@/components/CookieConsent";

const GA_ID = "G-XJLWLENTH2";
const AW_ID = "AW-18379017947";

/**
 * Google Analytics + Google Ads — auto-consent model.
 *
 * All consent signals start as "granted" (set in layout.tsx beforeInteractive
 * and echoed here). GA4 and Google Ads fire with full measurement from the
 * first pageview.
 *
 * If a user opts out of advertising cookies via Cookie Settings, the
 * ConsentModeDefaults component pushes a consent update that revokes
 * ad_storage / ad_user_data / ad_personalization while keeping
 * analytics_storage granted.
 *
 * @see https://developers.google.com/tag-platform/security/guides/consent?consentmode=advanced
 */
export default function GoogleAnalytics() {
  // Listen for opt-out: if user disables ad cookies, revoke ad consent
  useEffect(() => {
    const stored = getCookieConsent();
    if (stored === "essential") {
      revokeAdConsent();
    }

    const onConsent = (e: Event) => {
      const detail = (e as CustomEvent<{ value: ConsentValue }>).detail;
      if (detail?.value === "essential") {
        revokeAdConsent();
      } else if (detail?.value === "all") {
        grantAllConsent();
      }
    };

    window.addEventListener("mc:cookie-consent", onConsent);
    return () => window.removeEventListener("mc:cookie-consent", onConsent);
  }, []);

  // Sync if another tab updates consent
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === COOKIE_CONSENT_KEY) {
        if (e.newValue === "essential") {
          revokeAdConsent();
        } else if (e.newValue === "all") {
          grantAllConsent();
        }
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return (
    <>
      <Script src={`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`} strategy="afterInteractive" />
      <Script id="gtag-init" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}

          // Consent Mode v2 — auto-consent: start granted
          gtag('consent', 'default', {
            analytics_storage: 'granted',
            ad_storage: 'granted',
            ad_user_data: 'granted',
            ad_personalization: 'granted',
          });

          gtag('js', new Date());
          gtag('config', '${GA_ID}', {
            send_page_view: true,
          });
          gtag('config', '${AW_ID}');
        `}
      </Script>
    </>
  );
}

/** Revoke ad-related consent — user opted out of advertising cookies. */
function revokeAdConsent() {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage: "granted",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
  }
}

/** Re-grant all consent — user re-enabled advertising cookies. */
function grantAllConsent() {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage: "granted",
      ad_storage: "granted",
      ad_user_data: "granted",
      ad_personalization: "granted",
    });
  }
}
